/**
 * The landing hero's three beats from *The Password Reset* — the call, the text that delivers the
 * code, and the room's vote — as the same shapes the room sends a real session. The hero renders
 * them through the real `PhoneCall`, `ArtifactCard` and `VoteBars`, so it can never drift from what
 * a session looks like. The copy is English on purpose: it is a picture of a scenario (the block is
 * `aria-hidden`, and its visually hidden caption is the translated description).
 */
import type { BarOption } from "../components/game";
import type { PlayerCall, PlayerNarrative } from "../lib/scenario";

/**
 * The call, three lines in. The fourth line is the one the caller is about to say: it is never
 * rendered, but its presence keeps the call live (`revealedLines < transcript.length`), which is
 * what shows the waveform.
 */
export const HERO_CALL: PlayerCall = {
  type: "call",
  title: "Incoming call: 'Jordan Meyer'",
  caller: { name: "Jordan Meyer", displayNumber: "+1 (312) 555-0148" },
  callee: { name: "Help desk", roleId: null },
  transcript: [
    {
      speaker: "caller",
      name: null,
      text: "I've got the board pre-read at nine thirty. I just need the MFA reset.",
      pauseMs: null,
    },
    { speaker: "callee", name: null, text: "Our process is to verify before we touch MFA.", pauseMs: null },
    { speaker: "caller", name: null, text: "Is there any way we can move fast on this one?", pauseMs: null },
    {
      speaker: "caller",
      name: null,
      text: "A code just went to my phone. Can you read it back to me?",
      pauseMs: null,
    },
  ],
};

/** Lines of `HERO_CALL` on show: all but the last, so the call is still live. */
export const HERO_CALL_LINES = 3;

/** The text that arrives while the caller is still talking. */
export const HERO_TEXT: NonNullable<PlayerNarrative["artifact"]> = {
  kind: "sms",
  from: "Northwind IT",
  timestamp: "09:21",
  body: "Your Northwind sign-in code is 482 913. IT will never ask you for this code.",
};

/** The vote in progress: labels only, because quality is never shown while a vote is open. */
export const HERO_VOTE: { options: BarOption[]; tally: Record<string, number> } = {
  options: [
    { id: "a", label: "Reset the MFA now" },
    { id: "b", label: "Call back on the HR number" },
    { id: "c", label: "Ask the security questions" },
  ],
  tally: { a: 3, b: 8, c: 1 },
};
